export const timeRanges = ['24h', '3d', '7d', '30d', '1y'] as const;

export type TimeRange = (typeof timeRanges)[number];
export type ChartParam = 'answer' | 'abandon' | 'ai_resolution' | 'agent_handoff' | 'asa';
export type ChartSmsParam = 'ai_resolution' | 'agent_handoff' | 'asa';

export type DashboardFilters = {
  time_range?: TimeRange;
  company_name?: string | undefined;
  start_time?: string;
  end_time?: string;
  time_zone?: string;
};

export type CallMetricsChartParams = {
  time_range?: TimeRange;
  chart_params?: ChartParam;
  start_time?: string;
  end_time?: string;
  time_zone?: string;
  company_name?: string;
};

export type SmsMetricsChartParams = {
  time_range?: TimeRange;
  chart_params?: ChartSmsParam;
  start_time?: string;
  end_time?: string;
  time_zone?: string;
  company_name?: string;
};

export type TrendingTopicsExportParams = {
  start_time?: string;
  end_time?: string;
  time_range?: TimeRange;
};

export type CallMetricsChart = {
  chart: AiPerformanceChartPoint[];
};

export type SmsMetricsChart = {
  chart: AiPerformanceChartPoint[];
};

// Null `change` means no baseline to compare against, which is not the same as no
// movement and must not render as zero.
export type Metric = {
  value: number;
  change: number | null;
};

// Its `change` is a percentage-point difference of two shares, so it always lands
// on a number. Overridden rather than inherited, or widening Metric would make this
// nullable too and the distinction would be lost.
type PercentageMetric = Omit<Metric, 'change'> & {
  current_percent: number;
  change: number;
};

export type CallMetricChartPoint = {
  date: string;
  ai: number;
  agent: number;
};

export type CallMetrics = {
  inbound_calls: Metric;
  answer: PercentageMetric;
  abandon: PercentageMetric;
  ai_resolution: PercentageMetric;
  agent_handoff: PercentageMetric;
  asa: Metric;
  // Optional: not every deployment carries the aggregate yet, and an absent key means
  // "not available here", never zero.
  out_of_hours_calls?: Metric;
  // Optional like the leaf above, and in seconds. Zero means no data, not a zero mean.
  average_call_duration?: Metric;
  // 24 positional slots, one per hour of the local day, and dense: a gap is a real zero.
  // Optional for the same reason as the two leaves above.
  hourly_calls?: number[];
  chart: CallMetricChartPoint[];
};

/** A queue row's provenance: a named target, the no-target bucket, or the folded tail. */
export type CallbackQueueKind = 'unit' | 'unassigned' | 'other';

/** `department` is null on both `unassigned` and `other`, so `kind` separates them. */
export type CallbackQueueDepartment = {
  kind: CallbackQueueKind;
  department_id: number | null;
  department: string | null;
  cases: number;
};

/** A null urgency is a case that arrived without one, which is not the same as low. */
export type CallbackQueueUrgency = {
  urgency: 'high' | 'medium' | 'low' | null;
  cases: number;
};

export type CallbackQueue = {
  total: number;
  unassigned: number;
  by_department: CallbackQueueDepartment[];
  by_urgency: CallbackQueueUrgency[];
};

export type SmsMetrics = {
  inbound_sms: Metric;
  answer: PercentageMetric;
  abandon: PercentageMetric;
  ai_resolution: PercentageMetric;
  agent_handoff: PercentageMetric;
  asa: Metric;
  chart: CallMetricChartPoint[];
};

export type Insight = {
  [key: string]: number | null;
};

/**
 * The analytics leg emits an object per language, with a null count for one the window
 * never saw. The agent leg flattens each entry to a bare count.
 */
export type InsightLanguageDistribution = {
  [key: string]:
    | {
        count: number | null;
        language_flag_url: string | null;
      }
    | number;
};

export type InsightOverallSatisfaction = {
  ai: number | null;
  agent: number | null;
  overall: number | null;
  responses: number | null;
  change: number;
};

export type Insights = {
  trending_topics: Insight;
  language_distribution: InsightLanguageDistribution;
  overall_satisfaction: InsightOverallSatisfaction;
};

export type AiPerformance = {
  number_of_responses: AiPerformanceMetric;
  number_of_unanswered: AiPerformanceMetric;
  average_ai_handling_time: AiPerformanceMetric;
  average_response_time: AiPerformanceMetric;
  chart: {
    number_of_responses: AiPerformanceChartPoint[];
    unanswered_chart: AiPerformanceChartPoint[];
    average_ai_handling_time: AiPerformanceChartPoint[];
    average_response_time_chart: AiPerformanceChartPoint[];
  };
};

export type AiPerformanceMetric = {
  value: number;
  change: number | null;
};

export type AiPerformanceChartPoint = {
  date: string;
  value: number | string;
};
